<template>
  <transition name="loading-fade">
    <div v-if="loading" class="loading-overlay">
      <div class="loading-content">
        <!-- Spinning Fire Ring -->
        <div class="loading-spinner">
          <svg class="spinner-svg" viewBox="0 0 36 36">
            <defs>
              <linearGradient
                id="loadingGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" style="stop-color:#8b0000;stop-opacity:1" />
                <stop offset="25%" style="stop-color:#dc2626;stop-opacity:1" />
                <stop offset="50%" style="stop-color:#ff4500;stop-opacity:1" />
                <stop offset="75%" style="stop-color:#ff6b00;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#ffa500;stop-opacity:1" />
              </linearGradient>
            </defs>
            <path
              class="spinner-bg"
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <path
              class="spinner-fill"
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <div class="spinner-center">
            <div class="fire-emoji">🔥</div>
          </div>
        </div>

        <!-- Loading Text -->
        <div class="loading-text">
          <div class="loading-title">{{ title }}</div>
          <div class="loading-subtitle">{{ subtitle }}</div>
        </div>

        <!-- Loading Dots -->
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'LoadingOverlay',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'กำลังโหลด...'
    },
    subtitle: {
      type: String,
      default: 'กรุณารอสักครู่'
    }
  }
}
</script>

<style lang="scss" scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(10, 10, 10, 0.85);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  text-align: center;
  padding: 40px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  min-width: 280px;

  @media (max-width: 768px) {
    padding: 30px;
    gap: 20px;
    min-width: 240px;
  }
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
}

.spinner-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  animation: spin 2s linear infinite;
}

.spinner-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.1);
  stroke-width: 2;
}

.spinner-fill {
  fill: none;
  stroke: url(#loadingGradient);
  stroke-width: 2;
  stroke-dasharray: 75, 100;
  stroke-linecap: round;
  animation: progress 1.5s ease-in-out infinite;
}

.spinner-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.fire-emoji {
  font-size: 24px;
  animation: fire-flicker 0.8s ease-in-out infinite alternate;

  @media (max-width: 768px) {
    font-size: 20px;
  }
}

.loading-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.loading-title {
  color: #fff;
  font-size: 18px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 16px;
  }
}

.loading-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 12px;
  }
}

.loading-dots {
  display: flex;
  gap: 8px;
  justify-content: center;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(45deg, #ff4500, #ffa500);
    animation: dots 1.4s ease-in-out infinite both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }
    &:nth-child(2) {
      animation-delay: -0.16s;
    }
    &:nth-child(3) {
      animation-delay: 0s;
    }
  }
}

// Animations
@keyframes spin {
  from {
    transform: rotate(-90deg);
  }
  to {
    transform: rotate(270deg);
  }
}

@keyframes progress {
  0% {
    stroke-dasharray: 0, 100;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 75, 100;
    stroke-dashoffset: -15;
  }
  100% {
    stroke-dasharray: 75, 100;
    stroke-dashoffset: -90;
  }
}

@keyframes fire-flicker {
  0% {
    transform: scale(1) rotate(0deg);
    filter: brightness(1);
  }
  100% {
    transform: scale(1.1) rotate(2deg);
    filter: brightness(1.2);
  }
}

@keyframes dots {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
}

// Transition animations
.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: all 0.3s ease;
}

.loading-fade-enter,
.loading-fade-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

.loading-fade-enter .loading-content,
.loading-fade-leave-to .loading-content {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}
</style>
